import * as React from 'react'
import EnterpriseIcon, {
  SearchIcon,
  CancelIcon,
  DownloadIcon,
} from '@enterprise-ui/icons'
import { Table, Tooltip, Button, Input } from '@enterprise-ui/canvas-ui-react'
import { removeReactElements } from 'util/CommonUtils'
import { isEqual, isEmpty } from 'lodash'
import { useState } from 'react'
import { hasFields } from 'util/Filter'

const getRowsPerPageOptions = (
  showAllRows,
  totalRowsCount,
  isAllRowsRequired
) => {
  switch (true) {
    case showAllRows && totalRowsCount > 1000:
      return [10, 25, 50, 100, 1000, totalRowsCount]
    case showAllRows && totalRowsCount > 100:
      return [10, 25, 50, 100, totalRowsCount]
    case showAllRows && isAllRowsRequired:
      return [10, 25, 50, 100, totalRowsCount]
    default:
      return [10, 25, 50, 100]
  }
}

const getClickableColumnData = (
  data = [],
  clickableColumn = [],
  onColumnClick = () => {}
) => {
  if (clickableColumn.length > 0) {
    return data.map((row) => {
      const newRow = { ...row }
      clickableColumn.forEach((key) => {
        if (key in newRow) {
          newRow[key] = {
            cellDisplay: (
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  onColumnClick(removeReactElements(newRow))
                }}
              >
                {newRow[key]}
              </div>
            ),
          }
        }
      })
      return newRow
    })
  }
  return data
}

const CustomCanvasTableComponent = ({
  columnData = [],
  data = [],
  sortable = false,
  scrollableWidth = false,
  currentPage = 1,
  rowsPerPage = 10,
  totalRowsCount = 0,
  showAllRows = false,
  isAllRowsRequired = false,
  handleRowSelect = () => {},
  selectionEnabled = false,
  paginationAtApiLevel = false,
  onCheckBoxChange = () => {},
  clickableColumn = [],
  showPopup = () => {},
  onRequestPage = () => {},
  onPageSizeChange = () => {},
  downloadFunction = () => {},
  onSortSelectedChange = () => {},
  additionalFiltersContent = null,
  selected = [],
  disablePaging = false,
  additionalActions = false,
  enableDownload = false,
  enableSearch = false,
  enableMultiSearch = false,
  paginationlocation = '',
  isLoading = false,
  ...rest
}) => {
  const [searchText, setSearchText] = useState('')
  const [searchOpen, setsearchOpen] = useState(false)

  const [filterData, setFilterData] = useState(data || [])

  const handleChange = (event) => {
    let filterValue = event.target.value
    let searchQuery = filterValue
    if (enableMultiSearch) {
      try {
        searchQuery = filterValue
          ?.replace(/[/+//()/[\\]/g, '\\$&') // Escapes special characters
          ?.trim()
          .split(/[,\r\n]+/g) // Regex to split by comma and newline
          ?.filter(String) // removes empty string from search list
        const wholeString = searchQuery?.join(' ') // Add whole string in case space separated string was searched
        searchQuery.unshift(wholeString)
      } catch (e) {
        console.log('Error in special character search', e)
      }
    }
    setSearchText(filterValue)
    filterDataFunction(searchQuery)
  }

  const filterDataFunction = (searchQuery = '') => {
    let result = [...data]
    if (searchQuery) {
      if (
        enableMultiSearch &&
        Array.isArray(searchQuery) &&
        searchQuery.length > 0
      ) {
        // If list of search query
        result = data.filter((item) =>
          searchQuery.some((value) => hasFields(item, value))
        )
      } else if (!enableMultiSearch) {
        // If single search query
        result =
          data.filter((i) => {
            return hasFields(i, searchQuery)
          }) || []
      }
    }
    setFilterData(result)
  }

  // Update filterData if data prop changes
  React.useEffect(() => {
    if (!isEqual(filterData, data || [])) {
      setFilterData(data || [])
    }
  }, [data])

  return (
    <div className="table-container">
      <div className="table-toolbar" style={{ display: 'flex' }}>
        <div>{additionalActions ? additionalFiltersContent() : <></>}</div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {enableSearch ? (
            <div style={{ position: 'relative' }}>
              <Tooltip
                style={{
                  display: searchOpen === false ? 'block' : 'none',
                  height: '45px',
                  width: '45px',
                }}
                content="Search list"
                location="bottom"
              >
                <Button
                  type="ghost"
                  iconOnly
                  style={{
                    height: '45px',
                    width: '45px',
                  }}
                >
                  <EnterpriseIcon
                    onClick={() => setsearchOpen(true)}
                    icon={SearchIcon}
                  />
                </Button>
              </Tooltip>
              <Input.Text
                size="dense"
                className="table-search-bar"
                onChange={handleChange}
                placeholder="Search"
                value={searchText}
                style={{
                  display:
                    searchOpen || !isEmpty(searchText) ? 'block' : 'none',
                  width: '400px',
                  height: '45px',
                }}
              />
              <Button
                type="ghost"
                className="search-clear-button icon-button"
                id="clearall"
                style={{
                  display: searchOpen ? 'flex' : 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                size="small"
                onClick={() => {
                  filterDataFunction()
                  setsearchOpen(false)
                  setSearchText('')
                }}
                iconOnly
              >
                <EnterpriseIcon icon={CancelIcon} />
              </Button>
            </div>
          ) : (
            <></>
          )}
          {enableDownload ? (
            <Button
              type="ghost"
              iconOnly
              style={{
                height: '45px',
                width: '45px',
              }}
            >
              <EnterpriseIcon
                onClick={() => downloadFunction()}
                icon={DownloadIcon}
              />
            </Button>
          ) : (
            <></>
          )}
        </div>
      </div>
      <div className="table-content">
        <Table
          data={{
            defaultColDef: {
              sortable: sortable,
              onSort:(order,column)=>{onSortSelectedChange(order,column,'')}
            },
            columnDefs: columnData,
            rowData: isLoading
              ? []
              : clickableColumn
                ? getClickableColumnData(filterData, clickableColumn, showPopup)
                : filterData,
          }}
          showGrid={false}
          {...(!disablePaging && {
            pagination: {
              location: paginationlocation || 'bottom',
              pageSize: rowsPerPage,
              currentPage: currentPage,
              type: paginationAtApiLevel ? 'server' : 'client',
              pageSelector: true,
              pageSizeOptions: getRowsPerPageOptions(
                showAllRows,
                totalRowsCount,
                isAllRowsRequired
              ),
              totalItems: totalRowsCount,
              onRequestFirst: () => onRequestPage(1),
              onRequestLast: ({ totalPages }) => onRequestPage(totalPages),
              onRequestNext: () => onRequestPage(currentPage + 1),
              onRequestPrev: () => onRequestPage(currentPage - 1),
              onRequestPage: ({ newPage }) => onRequestPage(newPage),
              onPageSizeChange: ({ newPageSize }) =>
                onPageSizeChange(newPageSize),
            },
          })}
          scrollableWidth={scrollableWidth}
          {...(selectionEnabled && {
            rowSelection: 'multiple',
            onRowSelect: (rows) => {
              onCheckBoxChange(rows)
            },
            selectedRows: selected.map((item) => item.id),
          })}
          // {...rest}
        />
      </div>
    </div>
  )
}

const areEqual = (prevProps, nextProps) => {
  return (
    isEqual(prevProps.columnData, nextProps.columnData) &&
    isEqual(prevProps.data, nextProps.data) &&
    prevProps.sortable === nextProps.sortable &&
    prevProps.scrollableWidth === nextProps.scrollableWidth &&
    prevProps.currentPage === nextProps.currentPage &&
    prevProps.rowsPerPage === nextProps.rowsPerPage &&
    prevProps.totalRowsCount === nextProps.totalRowsCount &&
    prevProps.showAllRows === nextProps.showAllRows &&
    prevProps.isAllRowsRequired === nextProps.isAllRowsRequired &&
    isEqual(prevProps.selected, nextProps.selected) &&
    isEqual(prevProps.clickableColumn, nextProps.clickableColumn)
    // Note: Functions like handleRowSelect and showPopup are not compared,
    // as they are usually re-created on each render and would always fail equality.
  )
}

// Export a component that allows passing a custom areEqual function
const CustomCanvasTable = React.forwardRef((props, ref) => {
  const { areEqual: customAreEqual, ...rest } = props
  const MemoizedTable = React.useMemo(
    () => React.memo(CustomCanvasTableComponent, customAreEqual || areEqual),
    [customAreEqual]
  )
  return <MemoizedTable ref={ref} {...rest} />
})

export default CustomCanvasTable
